import * as Yup from 'yup';
import {TestContext, ValidationError} from 'yup';
import {
  validateContainsLatinCharacter,
  validatePassword,
  validatePhone,
  validatePhoneOrEmail,
  validatePostalCode,
} from 'src/utils/validations';

declare module 'yup' {
  interface StringSchema<T extends string | null | undefined = string> extends Schema<T> {
    phoneOrEmail: (message: string, onEmpty?: string | boolean) => StringSchema<T>;
    containsLatinCharacter: (message: string) => StringSchema<T>;
    phoneNumber: (message: string, onEmpty?: string | boolean) => StringSchema<T>;
    password: (message: string, onEmpty?: string | boolean) => StringSchema<T>;
    usPostalCode: (message?: string, onEmpty?: string | boolean) => StringSchema<T>;
  }

  interface ArraySchema<T> {
    unique: (message: string, getProperty?: (val: T) => T) => ArraySchema<T>;
  }

  interface NumberSchema<T extends number | null | undefined = number> {
    maxAwardable: () => NumberSchema<T>;
  }
}

function handleEmptyValue(
  this: TestContext,
  onEmpty: boolean | string = true
): boolean | ValidationError {
  return typeof onEmpty === 'string' ? this.createError({path: this.path, message: onEmpty}) : true;
}

function handleValidationFunction<T extends string>(
  this: TestContext,
  value: T,
  message: string,
  fn: (value: T) => $TSFixMe & {valid: boolean}
): boolean | ValidationError {
  const result = fn(value);
  return result.valid ? true : this.createError({path: this.path, message});
}

Yup.addMethod(
  Yup.string,
  'phoneOrEmail',
  function checkPhoneOrEmail(message, onEmpty: boolean | string = true) {
    return this.test('phoneOrEmail', message, function (value: string) {
      return !value
        ? handleEmptyValue.bind(this)(onEmpty)
        : handleValidationFunction.bind(this)(value, message, validatePhoneOrEmail);
    });
  }
);

/**
 * Validates phone number
 */
Yup.addMethod(
  Yup.string,
  'phoneNumber',
  function checkPhone(message, onEmpty: boolean | string = true) {
    return this.test('phoneNumber', message, function (value: string) {
      return !value
        ? handleEmptyValue.bind(this)(onEmpty)
        : handleValidationFunction.bind(this)(value, message, validatePhone);
    });
  }
);

const defaultGetter = (x: unknown): unknown => x;

/**
 * Validates password
 */

Yup.addMethod(Yup.string, 'password', function (message, onEmpty = true) {
  return this.test('password', message, function (value: string) {
    return !value
      ? handleEmptyValue.bind(this)(onEmpty)
      : handleValidationFunction.bind(this)(value, message, validatePassword);
  });
});

/**
 * Validates postal code or zip code
 */

Yup.addMethod(Yup.string, 'usPostalCode', function (message, onEmpty = true) {
  return this.test('usPostalCode', message, function (value: string) {
    return !value
      ? handleEmptyValue.bind(this)(onEmpty)
      : handleValidationFunction.bind(this)(
          value,
          message || 'Postal code must be 5 or 9 digits',
          validatePostalCode
        );
  });
});

/**
 * Allows checks on array uniqueness.
 * Can pass you're own get/transform method to pull properties with.
 * This will add an error to the first duplicated array item
 */
Yup.addMethod(Yup.array, 'unique', function checkUnique(message, getProperty = defaultGetter) {
  return this.test('unique', message, function (list: unknown[]) {
    const data = list.map(getProperty);
    const set = new Set(data);
    const isUnique = list.length === set.size;
    if (isUnique) return true;
    for (let i = 0; i < data.length; i++) {
      if (set.has(data[i])) {
        set.delete(data[i]);
      } else {
        return this.createError({path: `${this.path}.[${i}]`, message});
      }
    }

    // should never reach this point
    return false;
  });
});

Yup.addMethod(Yup.string, 'containsLatinCharacter', function containsLatinCharacter(message) {
  return this.test('containsLatinCharacter', message, function (value: string) {
    if (!value) return true;
    return validateContainsLatinCharacter(value)
      ? true
      : this.createError({path: this.path, message});
  });
});

Yup.addMethod(Yup.number, 'maxAwardable', function () {
  return this.test(
    'maxAwardable',
    'Max Award amount must be null or greater than or equal to the amount',
    function (value) {
      const {amount, tiered} = this.parent;
      return !value || tiered || value >= amount;
    }
  );
});

export default Yup;
